import { computed, ref, shallowRef } from 'vue'
import { defineStore } from 'pinia'
import type { Comic } from '@tanko/shared'
import {
  applyQueueReconciliation, enqueueComics, loadBackupQueue, recordQueueStatus, requeueFailed,
} from '../db/repo'
import { isPending, type BackupQueueEntry } from '../lib/backup-queue-entry'
import {
  orderQueue, reconcileQueue, runBackupQueue,
  type QueueOutcome, type QueueRunSummary,
} from '../lib/backup-queue'
import { isBackedUp } from '../lib/comic-sync'
import { useLibraryStore } from './library'
import { useCloudLibraryStore } from './cloud-library'
import { useUploadStore, type UploadPhase } from './upload'

/**
 * 常駐的備份佇列。
 *
 * 不重新實作上傳：逐本呼叫既有單本 upload store 的 prepare 與 start，並以它的 phase 與
 * 失敗分類判斷結果。一次只跑一本——單本內部已經對分片並行，再加一層只會讓分片互相排隊
 * 並把記憶體乘上並行數。
 *
 * 排程是自動的，執行不是：`requestPermission()` 必須在使用者手勢的呼叫堆疊內。
 */
export const useBackupQueueStore = defineStore('backup-queue', () => {
  const library = useLibraryStore()
  const cloud = useCloudLibraryStore()
  const upload = useUploadStore()

  const entries = shallowRef<BackupQueueEntry[]>([])
  const running = ref(false)
  const stopRequested = ref(false)
  const paused = ref(false)
  const pausedReason = ref('')
  const syncing = ref(false)
  /** 這次開啟頁面之後跑完過一輪。只為了讓完成摘要留在畫面上，不持久化。 */
  const finished = ref(false)
  /**
   * 上一輪的結果。已備份的作品在下一次排程時就不再留佇列項——真相是 syncState，
   * 佇列項只是重複記一次，留著會讓明細隨藏書一起無限長。跑了幾本因此由這裡記，不靠那張表。
   */
  const lastRun = shallowRef<QueueRunSummary>()
  /** 這一輪已經處理完的本數，用來顯示「第 X／N 本」。 */
  const position = ref(0)
  const total = ref(0)

  // await 之後 TypeScript 仍沿用 await 前的窄化結果，而 start() 會改變它；
  // 函式邊界讓型別回到完整的 union。
  const currentPhase = (): UploadPhase => upload.phase

  const byId = computed(() => new Map(entries.value.map(entry => [entry.comicId, entry])))
  const ordered = computed(() => orderQueue(entries.value, library.comics.map(comic => comic.id)))
  const pendingEntries = computed(() => ordered.value.filter(isPending))
  const pendingCount = computed(() => pendingEntries.value.length)
  const doneCount = computed(() => entries.value.filter(entry => entry.status === 'done').length)
  const failedEntries = computed(() => ordered.value.filter(entry => entry.status === 'failed'))
  const skippedCount = computed(() => entries.value.filter(entry => entry.status === 'skipped').length)
  const activeComicId = computed(() => ordered.value.find(entry => entry.status === 'running')?.comicId)
  const activeComic = computed(() => library.comics.find(comic => comic.id === activeComicId.value))
  /** 有事情可做才顯示面板：沒有待處理也沒有失敗紀錄時完全不佔版面。 */
  const visible = computed(() =>
    !!pendingCount.value || !!failedEntries.value.length || running.value || finished.value)

  async function refresh() {
    entries.value = await loadBackupQueue()
  }

  /** 依書庫現況補齊佇列。不啟動任何上傳。 */
  async function sync() {
    if (running.value || syncing.value) return
    syncing.value = true
    try {
      const stored = await loadBackupQueue()
      await applyQueueReconciliation(reconcileQueue(library.comics, stored, Date.now()))
      await refresh()
    } finally { syncing.value = false }
  }

  /** 跑一本：交給既有的單本上傳流程，只把結果翻譯成佇列的語彙。 */
  async function runOne(comicId: string): Promise<QueueOutcome> {
    const comic = library.comics.find(item => item.id === comicId)
    if (!comic) return { kind: 'skipped', reason: '作品已從書庫移除' }
    if (isBackedUp(comic)) return { kind: 'skipped', reason: '已經備份完成' }
    await upload.prepare(comic)
    if (currentPhase() !== 'ready') {
      return { kind: 'failed', reason: upload.error || '無法準備上傳', failure: upload.failureKind === 'systemic' ? 'systemic' : 'item' }
    }
    // 來源與既有 session 對不上時不替使用者選續傳或重來，留給單本面板決定。
    if (upload.sourceChanged) return { kind: 'failed', reason: upload.error || '來源與這次上傳建立時不同', failure: 'item' }
    await upload.start()
    if (currentPhase() !== 'complete') {
      return { kind: 'failed', reason: upload.error || '上傳未完成', failure: upload.failureKind === 'systemic' ? 'systemic' : 'item' }
    }
    return { kind: 'done' }
  }

  async function start() {
    if (running.value) return
    await sync()
    if (!pendingCount.value) return
    running.value = true
    finished.value = false
    lastRun.value = undefined
    stopRequested.value = false
    paused.value = false
    pausedReason.value = ''
    position.value = 0
    total.value = pendingCount.value
    try {
      const summary = await runBackupQueue({
        next: () => pendingEntries.value[0]?.comicId,
        run: async comicId => {
          position.value += 1
          return runOne(comicId)
        },
        record: async (comicId, status, reason) => {
          await recordQueueStatus(comicId, status, reason)
          await refresh()
        },
        stopRequested: () => stopRequested.value,
      })
      paused.value = summary.paused
      pausedReason.value = summary.pausedReason
      lastRun.value = summary
    } finally {
      running.value = false
      finished.value = true
      cloud.invalidate()
      await refresh()
    }
  }

  function requestStop() { if (running.value) stopRequested.value = true }

  /** 加入佇列並開始。`front` 為真時插到最前面，成為下一個執行對象。 */
  async function enqueue(comics: readonly Comic[], front = false): Promise<void> {
    const wanted = comics.filter(comic => !isBackedUp(comic))
    if (!wanted.length) return
    const earliest = pendingEntries.value[0]?.queuedAt ?? Date.now()
    await enqueueComics(wanted.map(comic => comic.id), front ? earliest - 1 : Date.now())
    await refresh()
    await start()
  }

  /** 重試失敗的項目；系統性暫停之後的「重試」也走這裡。 */
  async function retry(): Promise<void> {
    if (running.value) return
    await requeueFailed()
    paused.value = false
    pausedReason.value = ''
    await refresh()
    await start()
  }

  return {
    entries, ordered, running, stopRequested, paused, pausedReason, position, total, finished, lastRun,
    byId, pendingEntries, pendingCount, doneCount, failedEntries, skippedCount,
    activeComicId, activeComic, visible,
    refresh, sync, start, requestStop, enqueue, retry,
  }
})
